import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import type { Business } from "../../components/ui/cards/types";
import InventorySection from "../../components/ui/InventorySection";
import RimInventorySection from "../../components/ui/RimInventorySection";
import SubmitReviewModal from "../../components/reviews/SubmitReviewModal";
import { useAuthStore } from "../../stores/authStore";
import { useReviews } from "../../hooks/useReviews";

// Extend Business type for ShopPage to include missing fields
type ShopBusiness = Business & {
  imageUrl?: string;
  gallery?: string[];
  gallery_videos?: string[];
  shop_video_url?: string | null;
  reviews?: { user: string; comment: string; rating: number }[];
  business_hours?: Record<string, string>;
  services?: { name: string; icon?: string }[] | string[];
};


const GalleryCarousel: React.FC<{ images: string[]; onImageClick: (idx: number) => void }> = ({ images, onImageClick }) => {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef<number | null>(null);

  // Autoplay
  useEffect(() => {
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [images.length]);

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX > 40) {
      setIdx((i) => (i - 1 + images.length) % images.length);
    } else if (deltaX < -40) {
      setIdx((i) => (i + 1) % images.length);
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="relative w-full max-w-md mx-auto h-48 rounded-xl overflow-hidden bg-gray-200 flex items-center justify-center"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={images[idx]}
        alt={`Gallery ${idx + 1}`}
        className="w-full h-full object-cover transition-all duration-500"
        onClick={() => onImageClick(idx)}
        style={{ cursor: 'pointer' }}
      />
      {/* Indicators */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
        {images.map((_, i) => (
          <span
            key={i}
            className={`w-2 h-2 rounded-full ${i === idx ? 'bg-blue-600' : 'bg-blue-200'} opacity-80`}
          />
        ))}
      </div>
    </div>
  );
};

function formatTime(time: string) {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}

function getServiceIcon(serviceName: string): string {
  const name = serviceName.toLowerCase();
  if (name.includes('tyre') || name.includes('tire')) return '🛞';
  if (name.includes('alignment') || name.includes('wheel alignment')) return '⚖️';
  if (name.includes('balancing') || name.includes('wheel balancing')) return '⚖️';
  if (name.includes('fitment') || name.includes('fitting')) return '🔧';
  if (name.includes('repair') || name.includes('fix')) return '🛠️';
  if (name.includes('service') || name.includes('maintenance')) return '⚙️';
  if (name.includes('inspection') || name.includes('check')) return '🔍';
  if (name.includes('rotation')) return '🔄';
  if (name.includes('pressure')) return '💨';
  return '🔧'; // Default icon
}

const ShopPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Use ShopBusiness type for state, cast as needed for UI compatibility
  const [business, setBusiness] = useState<ShopBusiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Header image state (local only)
  const [headerImage, setHeaderImage] = useState<string | null>(null);

  // Reviews data
  const { reviews: businessReviews, loading: reviewsLoading, refetch: refetchReviews } = useReviews(business?.id || '');

  // Button handlers
  const handleCall = () => {
    if (business?.contact_phone) {
      window.open(`tel:${business.contact_phone}`, "_self");
    }
  };

  const handleDirections = () => {
    if (business?.address && business?.city) {
      const query = encodeURIComponent(`${business.address}, ${business.city}`);
      window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
    }
  };


  useEffect(() => {
    const fetchBusiness = async () => {
      setLoading(true);
      // Fetch business by slug
      const { data: businessData } = await supabase
        .from("businesses")
        .select("*")
        .eq("slug", slug || "")
        .eq("published", true)
        .single();

      // Attach deals to business object
      // Patch missing fields for UI compatibility
      setBusiness(
        businessData
          ? {
              ...(businessData as any),
              imageUrl: (businessData as any).logo_url || "",
              gallery: (businessData as any).gallery || [],
              gallery_videos: (businessData as any).gallery_videos || [],
              shop_video_url: (businessData as any).shop_video_url || null,
              reviews: (businessData as any).reviews || [],
              business_hours: (businessData as any).business_hours || {},
              services: (businessData as any).services || [],
            }
          : null
      );
      setLoading(false);
    };
    fetchBusiness();
  }, [user, slug]);

  // Always scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Set default header image when business/gallery loads
  useEffect(() => {
    if (business?.gallery && business.gallery.length > 0 && !headerImage) {
      setHeaderImage(business.gallery[0]);
    }
  }, [business, headerImage]);

  if (loading) return <div>Loading...</div>;
  if (!business) return <div>Business not found.</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex flex-col">
      {/* Hero Section */}
      <div className="relative w-full h-56 sm:h-64 md:h-80 bg-gray-200">
        <img
          src={headerImage || business.logo_url || business.imageUrl}
          alt={business.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        {/* Glassmorphism Card Overlay */}
        <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 z-20 w-full max-w-2xl px-2 sm:px-0">
          <div className="backdrop-blur-lg bg-white/60 rounded-3xl shadow-2xl px-4 py-6 flex flex-col items-center gap-2 border border-white/40 w-full">
            <div className="w-24 h-24 md:w-36 md:h-36 rounded-full border-4 border-white shadow-lg bg-white flex items-center justify-center overflow-hidden -mt-20 mb-2">
              <img
                src={business.logo_url || business.imageUrl}
                alt={business.name}
                className="object-cover w-24 h-24 md:w-36 md:h-36"
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 text-center">{business.name}</h1>
            <div className="text-gray-600 text-sm md:text-base flex items-center justify-center gap-1">
              <span>📍</span>
              <span>{business.address}, {business.city}</span>
            </div>
            
            {/* Mobile Action Buttons - now inside glass section */}
            <div className="flex md:hidden flex-row gap-2 mt-3 w-full justify-center px-2">
              <button
                className="flex-1 flex flex-col items-center justify-center gap-1 bg-blue-500/80 text-white px-3 py-2 rounded-lg font-bold shadow-lg hover:bg-blue-600/80 active:scale-95 transition text-xs focus:outline-none"
                onClick={handleCall}
                disabled={!business?.contact_phone}
                title={business?.contact_phone ? `Call ${business.contact_phone}` : "No phone number available"}
              >
                <span className="text-lg">📞</span>
                Call
              </button>
              <button
                className="flex-1 flex flex-col items-center justify-center gap-1 bg-gray-400/80 text-white px-3 py-2 rounded-lg font-bold shadow-lg hover:bg-gray-500/80 active:scale-95 transition text-xs focus:outline-none"
                onClick={handleDirections}
                disabled={!business?.address || !business?.city}
                title={business?.address && business?.city ? "Get Directions" : "No address available"}
              >
                <span className="text-lg">🧭</span>
                Directions
              </button>
            </div>
            
            {/* Desktop buttons */}
            <div className="hidden md:flex flex-row gap-3 mt-4 w-full justify-center">
              <button
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-3 rounded-xl font-bold shadow-lg hover:from-blue-600 hover:to-blue-800 active:scale-95 transition text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                onClick={handleCall}
                disabled={!business?.contact_phone}
                title={business?.contact_phone ? `Call ${business.contact_phone}` : "No phone number available"}
              >
                <span className="text-xl">📞</span>
                Call
              </button>
              <button
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-gray-200 to-gray-400 text-gray-900 px-4 py-3 rounded-xl font-bold shadow-lg hover:from-gray-300 hover:to-gray-500 active:scale-95 transition text-base focus:outline-none focus:ring-2 focus:ring-gray-400"
                onClick={handleDirections}
                disabled={!business?.address || !business?.city}
                title={business?.address && business?.city ? "Get Directions" : "No address available"}
              >
                <span className="text-xl">🧭</span>
                Directions
              </button>
            </div>
          </div>
        </div>
        {/* Header image selection info for business owner */}
        {/* Business owner header image info removed – only show if real logic is implemented */}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col mt-32 md:mt-40 px-2 max-w-full md:max-w-3xl w-full mx-auto">

        {/* Gallery - moved closer to glass section */}
        <div className="mt-1 mb-8" style={{ marginTop: '3px' }}>
          {/* Shop intro video */}
          {business.shop_video_url && (
            <div className="mb-4">
              <video
                src={business.shop_video_url}
                controls
                className="w-full rounded-xl shadow-md max-h-64 object-cover bg-black"
                poster={business.imageUrl || undefined}
              />
            </div>
          )}
          {/* Mobile swipeable */}
          <div className="md:hidden">
            {Array.isArray(business.gallery) && business.gallery.length > 0 ? (
              <GalleryCarousel images={business.gallery as string[]} onImageClick={setLightbox} />
            ) : !business.shop_video_url ? (
              <span className="text-gray-400">No gallery images available.</span>
            ) : null}
          </div>
          {/* Desktop standard */}
          <div className="hidden md:block">
            <div className="flex gap-4 justify-center flex-wrap">
              {Array.isArray(business.gallery) && business.gallery.length > 0 ? (
                (business.gallery as string[]).map((img: string, idx: number) => (
                  <div key={idx} className="relative group">
                    <img
                      src={img}
                      alt={`Gallery ${idx + 1}`}
                      className={`w-48 h-32 object-cover rounded-xl border-4 transition-all duration-200 ${headerImage === img ? 'border-blue-600' : 'border-transparent'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setLightbox(idx)}
                    />
                  </div>
                ))
              ) : !business.shop_video_url ? (
                <span className="text-gray-400">No gallery images available.</span>
              ) : null}
              {/* Additional gallery videos (desktop) */}
              {Array.isArray(business.gallery_videos) && (business.gallery_videos as string[]).map((vid: string, idx: number) => (
                <video
                  key={`vid-${idx}`}
                  src={vid}
                  controls
                  className="w-48 h-32 object-cover rounded-xl"
                />
              ))}
            </div>
          </div>
        </div>


        {/* Reviews Section */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-3 text-blue-700 px-2">Customer Reviews</h2>
          <div className="relative bg-gradient-to-br from-yellow-50 to-white rounded-2xl shadow-lg p-4 overflow-x-auto no-scrollbar">
            <div className="flex space-x-4 touch-pan-x snap-x snap-mandatory">
              {reviewsLoading ? (
                <div className="flex items-center justify-center min-w-[220px]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                  <span className="ml-2 text-gray-600">Loading reviews...</span>
                </div>
              ) : businessReviews && businessReviews.length > 0 ? (
                businessReviews.map((r: any, i: number) => (
                  <div
                    key={r.id || i}
                    className="bg-white rounded-xl shadow p-4 min-w-[220px] snap-center flex-shrink-0 border-2 border-yellow-200"
                    style={{ boxShadow: '0 4px 24px 0 rgba(255, 193, 7, 0.08)' }}
                  >
                    <div className="flex items-center mb-1">
                      <span className="font-semibold text-gray-800">{r.user_profiles?.full_name || 'Anonymous'}</span>
                      <span className="ml-2 text-yellow-400">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                    </div>
                    <div className="text-gray-700 text-sm">{r.comment}</div>
                    {r.booking_id && (
                      <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        From Booking
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <span className="text-gray-400">No reviews available.</span>
              )}
            </div>
            {businessReviews && businessReviews.length > 0 && (
              <>
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                  {businessReviews.map((_, i: number) => (
                    <span key={i} className="w-2 h-2 rounded-full bg-yellow-300 opacity-70" />
                  ))}
                </div>
                <div className="absolute right-4 top-4 text-xs text-yellow-600 font-semibold bg-yellow-100 px-2 py-1 rounded-full shadow">Swipe</div>
              </>
            )}
          </div>
          {user && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowReviewModal(true)}
                className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium"
              >
                Write a Review
              </button>
            </div>
          )}
        </div>

        {/* Desktop-only: Current Deals below reviews - removed, now unified above */}

        {/* Tyre Inventory Section */}
        <InventorySection
          businessId={business.id}
          businessName={business.name}
          businessPhone={business.contact_phone || undefined}
          businessSlug={slug}
        />

        {/* Rim Inventory Section - only displays if shop has rims */}
        {business.id && (
          <RimInventorySection 
            businessId={business.id}
            businessName={business.name}
          />
        )}

        {/* Services Section */}
        {Array.isArray(business.services) && business.services.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
              <span>🔧</span>Services
            </h2>
            <div className="flex flex-wrap gap-3">
              {business.services.map((service: any, idx: number) => {
                const serviceName = typeof service === "string" ? service : service.name;
                const serviceIcon = typeof service === "object" && service.icon ? service.icon : getServiceIcon(serviceName);
                
                return (
                  <div key={idx} className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-full shadow-sm transition-colors border border-blue-200">
                    <span className="text-lg">{serviceIcon}</span>
                    <span className="font-medium text-blue-800 text-sm">{serviceName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Business Hours Card - Moved from header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
            <span>⏰</span>Business Hours
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: 'monday', label: 'Monday' },
              { key: 'tuesday', label: 'Tuesday' },
              { key: 'wednesday', label: 'Wednesday' },
              { key: 'thursday', label: 'Thursday' },
              { key: 'friday', label: 'Friday' },
              { key: 'saturday', label: 'Saturday' },
              { key: 'sunday', label: 'Sunday' }
            ].map(({ key, label }) => {
              const hours = business.business_hours?.[key];
              let display = 'Closed';
              if (hours && typeof hours === 'string' && hours.includes('-')) {
                const [open, close] = hours.split('-');
                // Sentinel for around-the-clock (used by online stores).
                if (open === '00:00' && (close === '24:00' || close === '23:59')) {
                  display = 'Open 24 hours';
                } else {
                  display = `${formatTime(open)} - ${formatTime(close)}`;
                }
              }
              return (
                <div key={key} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-800">{label}</span>
                  <span className="text-gray-700">{display}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Section - Equal Height Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Location Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-full">
            <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
              <span>🗺️</span>Location
            </h2>
            <div className="flex-1 flex flex-col">
              <div className="w-full h-48 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center mb-4">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    business.address + ", " + business.city
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full block"
                  title="View on Google Maps"
                >
                  <iframe
                    title="Google Map"
                    width="100%"
                    height="100%"
                    style={{ border: 0, pointerEvents: "none" }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(
                      business.address + ", " + business.city
                    )}&output=embed`}
                  />
                  <span className="sr-only">Open location in Google Maps</span>
                </a>
              </div>
              <p className="text-gray-700 text-sm mt-auto">
                {business.address}, {business.city}, {business.province}
              </p>
            </div>
          </div>

          {/* Contact & About Combined Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-full">
            <h2 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
              <span>📞</span>Contact & About
            </h2>
            <div className="flex-1 flex flex-col gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-blue-600">📱</span>
                  <span>{business.contact_phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <span className="text-blue-600">✉️</span>
                  <span>{business.contact_email}</span>
                </div>
                <button
                  onClick={handleDirections}
                  className="flex items-center gap-3 text-blue-600 font-medium hover:text-blue-800 transition-colors"
                >
                  <span>🧭</span>
                  <span>Get Directions</span>
                </button>
              </div>
              <div className="border-t pt-4 mt-auto">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span>ℹ️</span>About
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {business.description || "No description available."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightbox !== null && business?.gallery && Array.isArray(business.gallery) && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center" onClick={() => setLightbox(null)}>
          <img
            src={(business.gallery as string[])[lightbox]}
            alt={`Gallery ${lightbox + 1}`}
            className="max-w-full max-h-[80vh] rounded-2xl shadow-2xl border-4 border-white"
          />
        </div>
      )}

      {/* Booking/messaging/quote modals hidden for now - Call and Directions remain. */}

      {/* Submit Review Modal */}
      {showReviewModal && business && (
        <SubmitReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          businessId={business.id}
          businessName={business.name}
          onReviewSubmitted={() => {
            setShowReviewModal(false);
            refetchReviews(); // Refresh reviews after submission
          }}
        />
      )}

      {/* Hide scrollbar utility */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ShopPage;
